import { ReturningPaymentStatus } from "./Party/ContentContext";
import { ExpressionColors, MessageConfigData, PostConfigData, QuizPosition, QuizScores } from "./Party/PartyConfigData";
import { FanboxColorName } from "./Utility/Color";

export type UserActionType = 'share';
export type UserActionStatus = 'pending' | 'ready' | 'moderating' | 'scheduled' | 'live';

export type UserAction = {
  itemId: string;
  actionType: UserActionType;
  config: ShareActionConfig;
  title: string;
  bodyText: string;
  image: string;
  multiplier: number;
  buttonText: string;
  created: number;
  status: UserActionStatus;
  statusText: string;
  completed?: number;
}

export const CheckUserAction = (data: UserAction, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('UserAction.itemId is undefined');

  if (data.actionType === undefined)
    errors.push('UserAction.actionType is undefined');
  else if (data.actionType !== 'share')
    errors.push('UserAction.actionType bad value =' + data.actionType);

  if (data.title === undefined)
    errors.push('UserAction.title is undefined');

  if (data.bodyText === undefined)
    errors.push('UserAction.bodyText is undefined');

  if (data.image === undefined)
    errors.push('UserAction.image is undefined');

  if (data.multiplier === undefined)
    errors.push('UserAction.multiplier is undefined');

  if (data.buttonText === undefined)
    errors.push('UserAction.buttonText is undefined');

  if (data.created === undefined)
    errors.push('UserAction.created is undefined');

  if (data.status === undefined)
    errors.push('UserAction.status is undefined');
  else if (data.status !== 'pending' && data.status !== 'live' && data.status !== 'moderating' && data.status !== 'ready' && data.status !== 'scheduled') {
    errors.push("UserAction.status value is invalid : " + data.status);
  }

  if (data.statusText === undefined)
    errors.push('UserAction.statusText is undefined');
}

export type UserActionSource = 'quiz' | 'creative';

export type UserActionContext = {
  itemId: string;
  source: UserActionSource;
  sourceId: string;
  aiDescription?: string;
  aiPrompt?: string;
  quizPosition? : QuizPosition;
  quizScores? : QuizScores;
  metaDataArray?: string[];
  metaDataDictionary?: { [key: string]: string };
  currentArtist: string;
  upcomingArtist: string;
  currentTracks: string[];
  upcomingTracks: string[];
}

export const CheckUserActionContext = (data: UserActionContext, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push("UserActionContext.itemId is undefined");

  if (data.source === undefined)
    errors.push("UserActionContext.source is undefined");
  else if (data.source !== 'creative') {
    if (data.source !== 'quiz') {
      errors.push("UserActionContext.source invalid value: " + data.source);
    }
  }

  if (data.sourceId === undefined)
    errors.push("UserActionContext.sourceId is undefined");
  if (data.currentArtist === undefined)
    errors.push("UserActionContext.currentArtist is undefined");
  if (data.upcomingArtist === undefined)
    errors.push("UserActionContext.upcomingArtist is undefined");
  if (data.currentTracks === undefined)
    errors.push("UserActionContext.currentTracks is undefined");
  if (data.upcomingTracks === undefined)
    errors.push("UserActionContext.upcomingTracks is undefined");
}



export type ShareActionConfig = {
  titles: string[];
  texts: string[];
  images: string[];
  buttonTexts: string[];
  buttonAction?: string;
  parameters?: { [key: string]: string };
}

export const CheckShareActionConfig = (data: ShareActionConfig, errors: string[]) => {
  if (data.titles === undefined)
    errors.push('ShareActionConfig.titles is undefined');
  else if (data.titles.length === undefined)
    errors.push('ShareActionConfig.titles is empty');

  if (data.texts === undefined)
    errors.push('ShareActionConfig.texts is undefined');
  else if (data.texts.length === undefined)
    errors.push('ShareActionConfig.texts is empty');

  if (data.images === undefined)
    errors.push('ShareActionConfig.images is undefined');
  else if (data.images.length === undefined)
    errors.push('ShareActionConfig.images is empty');

  if (data.buttonTexts === undefined)
    errors.push('ShareActionConfig.buttonTexts is undefined');
  else if (data.buttonTexts.length === undefined)
    errors.push('ShareActionConfig.buttonTexts is empty');

}

export const CloneUserAction = (from: UserAction, to?: UserAction): UserAction => {
  if (to !== undefined) {
    to.itemId = from.itemId;
    to.actionType = from.actionType;
    to.config = CloneShareActionConfig(from.config);
    to.title = from.title;
    to.bodyText = from.bodyText;
    to.image = from.image;
    to.multiplier = from.multiplier;
    to.buttonText = from.buttonText;
    to.created = from.created;
    to.status = from.status;
    to.statusText = from.statusText;
    to.completed = from.completed;
    return to;
  }
  else {
    return {
      itemId: from.itemId,
      actionType: from.actionType,
      config: CloneShareActionConfig(from.config),
      title: from.title,
      bodyText: from.bodyText,
      image: from.image,
      multiplier: from.multiplier,
      buttonText: from.buttonText,
      created: from.created,
      status: from.status,
      statusText: from.statusText,
      completed: from.completed
    };
  }
}



export const CloneShareActionConfig = (from: ShareActionConfig): ShareActionConfig => {
  const titles = from.titles.slice(0);
  const texts = from.texts.slice(0);
  const images = from.images.slice(0);
  const buttonTexts = from.buttonTexts.slice(0);
  const parameters: { [key: string]: string } | undefined = from.parameters === undefined ? undefined : (() => {
    const ret: { [key: string]: string } = {};
    const fp = from.parameters;
    Object.keys(from.parameters).forEach((key) => {
      ret[key] = fp[key];
    });
    return ret;
  })();
  return {
    titles: titles,
    texts: texts,
    images: images,
    buttonTexts: buttonTexts,
    buttonAction: from.buttonAction,
    parameters: parameters
  };
}

export type UserSubmission = {
  itemId: string;
  actionId: string;
  submitted: number;
}

export type UserMessageInstance = {
  time: number;
  message: MessageConfigData;
}

export type CognitoAuthProvider = 'Cognito' | 'Fanbox' | 'none' | 'Google';

export type CognitoIdentity = {
  id: string;
  email: string;
  nickname: string;
  accessToken: string;
  provider: CognitoAuthProvider;
};

export const EmptyUserData = (userid: string): UserData => {
  return {
    fanbox: { id: userid },
    //messages: {},
    posts: {},
    actions: [],
    userInformation: {
      nickname: '@anon',
      displayColor: ExpressionColors[0],
      displayIcon: '',
      email: '',
      emailVerified: false,
      signUpComplete: false
    },
    submissions: [],
    accountInformation: {
      boostBalance: 0,
      commentLikes: 0,
      comments: 0,
      posts : 0,
      postLikes : 0,
      postReplies : 0,
      fandomPoints: 0,
    },
    interests: {}
  };
}

export const EmptyCognitoIdentity = (): CognitoIdentity => {
  return {
    id: crypto.randomUUID(),
    nickname: "@anon",
    email: '',
    accessToken: 'INVALID',
    provider: 'none'
  }
}

export type FanboxIdentity = {
  id: string;
  accessToken?: string;
}

export type UserInformation = {
  nickname: string;
  email: string;
  emailVerified: boolean;
  displayColor: string;
  displayIcon: string;
  signUpComplete: boolean;
}

export type AccountInformation = {
  boostBalance: number;
  comments: number;
  commentLikes: number;
  posts: number;
  postLikes: number;
  postReplies: number;
  fandomPoints: number;
}


export type InterestProfileType = 'kvp' | 'vector' | 'matrix';
export type InterestProfile = {
  id: string;
  profileType: InterestProfileType,
  kvps: { [key: string]: number };
  enumerations: { [key: string]: { index: number, value: string }[] };
  metricNames: string[],
  metricNames2: string[],
  vector: number[];
  matrix: number[][];
}

export type SignUpData = {
  nickname: string;
  email: string;
  termsAccepted: boolean;
}

export type UserData = {
  cognito?: CognitoIdentity;
  fanbox: FanboxIdentity;
  userInformation: UserInformation;
  accountInformation: AccountInformation;
  //messages: { [key: string]: UserMessageInstance[] };
  posts: { [key: string]: UserMessageInstance[] };
  actions: UserAction[];
  submissions: UserSubmission[];
  interests: { [key: string]: InterestProfile };
  comments?: { [key: string]: UserMessageInstance[] };
  actionContexts?: UserActionContext[];
  returningPaymentStatus? : ReturningPaymentStatus;
}